import { inject, Injectable } from '@angular/core';
import { map, Observable, of } from 'rxjs';

import {
    CsvAggregatedInsightsChart,
    CsvAsStringArrays,
    CsvInsightChart,
    getPlatformKeysWithFollowers,
    PlatformKey,
} from '@malou-io/package-utils';

import { LightRestaurant } from '#modules/restaurants/models/restaurant';
import { RestaurantListItem } from '#modules/restaurants/models/restaurant-list-item';
import { AggregatedBoostersGiftsCsvInsightService } from '#modules/statistics-boosters/services/aggregated-csv/aggregated-boosters-gifts.service';
import { AggregatedBoostersReviewsCountCsvInsightV2Service } from '#modules/statistics-boosters/services/aggregated-csv/aggregated-boosters-reviews-count-v2.service';
import { AggregatedBoostersScanCountCsvInsightV2Service } from '#modules/statistics-boosters/services/aggregated-csv/aggregated-boosters-scan-count-v2.service';
import { AggregatedAverageReviewsRatingsCsvInsightsService } from '#modules/statistics-e-reputation/services/aggregated-csv/aggregated-average-reviews-ratings.service';
import { AggregatedPlatformsRatingsCsvInsightsService } from '#modules/statistics-e-reputation/services/aggregated-csv/aggregated-platforms-ratings.service';
import { AggregatedReviewCountCsvInsightsService } from '#modules/statistics-e-reputation/services/aggregated-csv/aggregated-review-count.service';
import { AggregatedSemanticAnalysisByCategoryCsvInsightsService } from '#modules/statistics-e-reputation/services/aggregated-csv/aggregated-semantic-analysis-by-category-insights.service';
import { AggregatedSemanticAnalysisCategoryPerformanceInsightsService } from '#modules/statistics-e-reputation/services/aggregated-csv/aggregated-semantic-analysis-category-performance-insights.service';
import { AggregatedSemanticAnalysisParentTopicSummaryInsightsService } from '#modules/statistics-e-reputation/services/aggregated-csv/aggregated-semantic-analysis-parent-topic-summary-insights.service';
import { AggregatedSemanticAnalysisTopTopicsCsvInsightsService } from '#modules/statistics-e-reputation/services/aggregated-csv/aggregated-semantic-analysis-top-topics-insights.service';
import { AggregatedFollowersCsvInsightsService } from '#modules/statistics-seo/services/aggregated-csv/aggregated-followers.service';
import { AggregatedGmbVisibilityCsvInsightsServiceV2 } from '#modules/statistics-seo/services/aggregated-csv/aggregated-gmb-visibility-v2.service';
import { AggregatedKeywordSearchImpressionsService } from '#modules/statistics-seo/services/aggregated-csv/aggregated-keyword-search-impressions.service';
import { AggregatedKeywordsService } from '#modules/statistics-seo/services/aggregated-csv/aggregated-keywords.service';
import { AggregatedPublicationsCsvInsightsServiceV2 } from '#modules/statistics-social-networks/services/aggregated-csv/aggregated-publications-v2.service';
import { AggregatedStoriesCsvInsightsService } from '#modules/statistics-social-networks/services/aggregated-csv/aggregated-stories.service';
import { AbstractDownloadCsvInsights } from '#modules/statistics/components/download-insights-modal/services/download-csv-insights.abstract.service';
import { CsvStringCreator } from '#shared/helpers/create-csv-string';

interface Params {
    csvInsightChart: CsvAggregatedInsightsChart;
    restaurantsList: RestaurantListItem[];
}

@Injectable()
export class DownloadAggregatedCsvInsightsService extends AbstractDownloadCsvInsights<Params> {
    // Utils
    private readonly _csvStringCreator = inject(CsvStringCreator);

    // AGGREGATED SEO
    private readonly _aggregatedFollowersCsvInsightsService = inject(AggregatedFollowersCsvInsightsService);
    private readonly _aggregatedGmbVisibilityCsvInsightsServiceV2 = inject(AggregatedGmbVisibilityCsvInsightsServiceV2);
    private readonly _aggregatedKeywordSearchImpressionsService = inject(AggregatedKeywordSearchImpressionsService);
    private readonly _aggregatedKeywordsService = inject(AggregatedKeywordsService);

    // AGGREGATED SOCIAL NETWORKS
    private readonly _aggregatedStoriesCsvInsightsService = inject(AggregatedStoriesCsvInsightsService);
    private readonly _aggregatedPublicationsCsvInsightsServiceV2 = inject(AggregatedPublicationsCsvInsightsServiceV2);

    // AGGREGATED E-REPUTATION
    private readonly _aggregatedPlatformsRatingsCsvInsightsService = inject(AggregatedPlatformsRatingsCsvInsightsService);
    private readonly _aggregatedReviewCountCsvInsightsService = inject(AggregatedReviewCountCsvInsightsService);
    private readonly _aggregatedAverageReviewsRatingsCsvInsightsService = inject(AggregatedAverageReviewsRatingsCsvInsightsService);
    private readonly _aggregatedSemanticAnalysisTopTopicsCsvInsightsService = inject(AggregatedSemanticAnalysisTopTopicsCsvInsightsService);
    private readonly _aggregatedSemanticAnalysisByCategoryInsightsService = inject(AggregatedSemanticAnalysisByCategoryCsvInsightsService);
    private readonly _aggregatedSemanticAnalysisParentTopicSummaryInsightsService = inject(
        AggregatedSemanticAnalysisParentTopicSummaryInsightsService
    );
    private readonly _aggregatedSemanticAnalysisCategoryPerformanceInsightsService = inject(
        AggregatedSemanticAnalysisCategoryPerformanceInsightsService
    );

    // AGGREGATED BOOSTERS
    private readonly _aggregatedBoostersScanCountCsvInsightV2Service = inject(AggregatedBoostersScanCountCsvInsightV2Service);
    private readonly _aggregatedBoostersGiftsCsvInsightsService = inject(AggregatedBoostersGiftsCsvInsightService);
    private readonly _aggregatedBoostersReviewsCountCsvInsightV2Service = inject(AggregatedBoostersReviewsCountCsvInsightV2Service);

    getCsvInsightsData$(params: Params): Observable<string | null> {
        const { csvInsightChart, restaurantsList } = params;
        const restaurants = this._getRestaurants(restaurantsList, csvInsightChart);
        let csvData$: Observable<CsvAsStringArrays | null>;
        switch (csvInsightChart) {
            case CsvInsightChart.AGGREGATED_GMB_VISIBILITY:
                csvData$ = this._aggregatedGmbVisibilityCsvInsightsServiceV2.getCsvData$({ restaurants });
                break;
            case CsvInsightChart.AGGREGATED_PLATFORMS_RATINGS:
                csvData$ = this._aggregatedPlatformsRatingsCsvInsightsService.getCsvData$({ restaurants });
                break;
            case CsvInsightChart.AGGREGATED_STORIES:
                csvData$ = this._aggregatedStoriesCsvInsightsService.getCsvData$({ restaurants });
                break;
            case CsvInsightChart.AGGREGATED_ALL_FOLLOWERS:
                csvData$ = this._aggregatedFollowersCsvInsightsService.getCsvData$({
                    platformKeys: getPlatformKeysWithFollowers(),
                    restaurants,
                });
                break;
            case CsvInsightChart.AGGREGATED_FB_FOLLOWERS:
                csvData$ = this._aggregatedFollowersCsvInsightsService.getCsvData$({ platformKeys: [PlatformKey.FACEBOOK], restaurants });
                break;
            case CsvInsightChart.AGGREGATED_IG_FOLLOWERS:
                csvData$ = this._aggregatedFollowersCsvInsightsService.getCsvData$({ platformKeys: [PlatformKey.INSTAGRAM], restaurants });
                break;
            case CsvInsightChart.AGGREGATED_TIKTOK_FOLLOWERS:
                csvData$ = this._aggregatedFollowersCsvInsightsService.getCsvData$({ platformKeys: [PlatformKey.TIKTOK], restaurants });
                break;
            case CsvInsightChart.AGGREGATED_PUBLICATIONS:
                csvData$ = this._aggregatedPublicationsCsvInsightsServiceV2.getCsvData$({ restaurants });
                break;
            case CsvInsightChart.AGGREGATED_REVIEW_COUNT:
                csvData$ = this._aggregatedReviewCountCsvInsightsService.getCsvData$();
                break;
            case CsvInsightChart.AGGREGATED_AVERAGE_REVIEWS_RATINGS:
                csvData$ = this._aggregatedAverageReviewsRatingsCsvInsightsService.getCsvData$();
                break;
            case CsvInsightChart.AGGREGATED_SEMANTIC_ANALYSIS_TOP_TOPICS:
                csvData$ = this._aggregatedSemanticAnalysisTopTopicsCsvInsightsService.getCsvData$({ restaurants });
                break;
            case CsvInsightChart.AGGREGATED_SEMANTIC_ANALYSIS_BY_CATEGORY:
                csvData$ = this._aggregatedSemanticAnalysisByCategoryInsightsService.getCsvData$({ restaurants });
                break;
            case CsvInsightChart.AGGREGATED_SEMANTIC_ANALYSIS_V2_PARENT_TOPIC_MENTION_SUMMARY:
                csvData$ = this._aggregatedSemanticAnalysisParentTopicSummaryInsightsService.getCsvData$();
                break;
            case CsvInsightChart.AGGREGATED_SEMANTIC_ANALYSIS_V2_CATEGORY_PERFORMANCE_SUMMARY:
                csvData$ = this._aggregatedSemanticAnalysisCategoryPerformanceInsightsService.getCsvData$();
                break;
            case CsvInsightChart.AGGREGATED_BOOSTERS_SCAN_COUNT:
                csvData$ = this._aggregatedBoostersScanCountCsvInsightV2Service.getCsvData$({ restaurants });
                break;
            case CsvInsightChart.AGGREGATED_BOOSTERS_WHEEL_OF_FORTUNE_GIFTS_DISTRIBUTION:
                csvData$ = this._aggregatedBoostersGiftsCsvInsightsService.getCsvData$({
                    restaurantsWithBoosterPackActivated: restaurants,
                });
                break;
            case CsvInsightChart.AGGREGATED_BOOSTERS_REVIEWS_COUNT:
                csvData$ = this._aggregatedBoostersReviewsCountCsvInsightV2Service.getCsvData$({ restaurants });
                break;
            case CsvInsightChart.AGGREGATED_TOP_SEARCH_KEYWORDS:
                csvData$ = this._aggregatedKeywordSearchImpressionsService.getCsvData$();
                break;
            case CsvInsightChart.AGGREGATED_RANKINGS:
                csvData$ = this._aggregatedKeywordsService.getCsvData$();
                break;
            default:
                return of(null);
        }
        return csvData$.pipe(
            map((csvData) => {
                if (!csvData) {
                    return null;
                }
                return this._csvStringCreator.create(csvData);
            })
        );
    }

    private _getRestaurants(restaurantsList: RestaurantListItem[], chart: CsvInsightChart): LightRestaurant[] {
        if (chart === CsvInsightChart.AGGREGATED_BOOSTERS_WHEEL_OF_FORTUNE_GIFTS_DISTRIBUTION) {
            return restaurantsList.filter((restaurant) => restaurant.boosterPackActivated).map((r) => r.toLightRestaurant());
        }
        return restaurantsList.map((r) => r.toLightRestaurant());
    }
}
